import { useEffect, useRef } from 'react'

import { isOwnPrivateColumn } from '~shared/rundown/column'

import { useUser } from '@/entities/user/api/hooks'

import { addPrivateNotesColumn } from '@/features/rundown/column/column.api'
import { getColumn } from '@/features/rundown/data/columns.store'
import { getRundown } from '@/features/rundown/data/rundown.store'

/**
 * Every signed-in user gets a personal "Private notes" column in each rundown
 * they open. Creates it once if missing; the column then arrives through the
 * columns listener like any other.
 *
 * Mount only once the session is ready, so the stores reflect the server (an
 * empty order means "no columns", not "not loaded yet").
 *
 * @remarks
 *   Reads the raw stores once instead of subscribing: the check runs a single
 *   time, so a subscription would only re-render the caller on every column
 *   change.
 */
export function useEnsurePrivateNotesColumn(rundownId: string) {
  const { data: user } = useUser()
  const handled = useRef(false)

  // useEffect: a one-shot API call triggered by data arrival (the user), not by
  // a user event, so there is no handler to put it in.
  useEffect(() => {
    if (handled.current) return
    if (!user) return

    handled.current = true

    const exists = (getRundown()?.columns ?? []).some((columnId) => {
      const column = getColumn(columnId)
      return !!column && isOwnPrivateColumn(column, user.uid)
    })
    // The request already toasted its error
    if (!exists) {
      addPrivateNotesColumn({ rundownId, privateUid: user.uid }).catch(() => {})
    }
  }, [rundownId, user])
}
